import React, { useEffect, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { httpGet } from '../../HttpRequestHandling/httpRequestHandler';
import { KINDS, toTimeline, countByKind } from './activityModel';

// New-UI Activity panel for the invoice editor: the change log, approval history and routing history in one timeline.
// Each source keeps the classic rule for who may see it (canViewChangeLog / canViewRouting come from the editor).
// A Drawer sits below MUI dialogs by default (zIndex.drawer), so it is raised above the editor's full-screen dialog.
const WHEN = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const get = (url, on) => (on ? httpGet(url).then(r => (Array.isArray(r.data) ? r.data : []), () => null) : Promise.resolve([]));

export default function ActivityPanel({ open, onClose, invoiceDocumentId, canViewChangeLog, canViewRouting }) {
	const [items, setItems] = useState(null);
	const [failed, setFailed] = useState(false);
	const [kind, setKind] = useState('all');

	useEffect(() => {
		if (!open || !invoiceDocumentId) return undefined;
		let live = true;
		setItems(null);
		setFailed(false);
		const base = '/api/invoicedocument/';
		Promise.all([
			get(`${base}invoicechangelog/${invoiceDocumentId}`, canViewChangeLog),
			get(`${base}invoicedocumentroutinghistory/${invoiceDocumentId}/`, true),
			get(`${base}invoicedocumentapprovalroutinghistory/${invoiceDocumentId}`, canViewRouting),
			get(`${base}invoicedocumentdiscrepantroutinghistory/${invoiceDocumentId}`, canViewRouting)
		]).then(([changes, approvals, routing, discrepant]) => {
			if (!live) return;
			setFailed([changes, approvals, routing, discrepant].some(x => x === null));
			setItems(toTimeline({ changes: changes || [], approvals: approvals || [], routing: routing || [], discrepant: discrepant || [] }));
		});
		return () => { live = false; };
	}, [open, invoiceDocumentId, canViewChangeLog, canViewRouting]);

	const counts = countByKind(items || []);
	const shown = (items || []).filter(it => kind === 'all' || it.kind === kind);
	const kinds = KINDS.filter(([k]) => k === 'all' || (k === 'change' ? canViewChangeLog : k === 'routing' ? canViewRouting : true));

	return (
		<Drawer anchor="right" open={!!open} onClose={onClose} sx={{ zIndex: t => t.zIndex.modal + 1 }} PaperProps={{ className: 'apw2 apw2-activity', role: 'dialog', 'aria-label': 'Activity' }}>
			<div className="apw2-activity-head">
				<h2>Activity</h2>
				<button type="button" className="apw2-iconbtn lg" aria-label="Close activity" onClick={onClose}><CloseIcon fontSize="inherit" /></button>
			</div>
			<div className="apw2-chips" role="group" aria-label="Filter activity">
				{kinds.map(([k, label]) => (
					<button type="button" key={k} className={'apw2-fchip' + (kind === k ? ' on' : '')} aria-pressed={kind === k} onClick={() => setKind(k)}>{label} <span className="n">{counts[k] || 0}</span></button>
				))}
			</div>
			{failed && <div className="apw2-activity-note">Some history could not be loaded.</div>}
			{items === null ? <div className="apw2-activity-empty"><CircularProgress size={20} /></div>
				: shown.length === 0 ? <div className="apw2-activity-empty">No activity yet.</div>
					: (
						<ol className="apw2-timeline">
							{shown.map(it => (
								<li key={it.key} className={'k-' + it.kind}>
									<div className="apw2-tl-top"><b>{it.title}</b><span className="apw2-mono">{it.at != null ? WHEN.format(new Date(it.at)) : (it.raw || '')}</span></div>
									{it.detail && <div className="apw2-tl-detail">{it.detail}</div>}
									{it.who && <div className="apw2-tl-who">{it.who}</div>}
								</li>
							))}
						</ol>
					)}
		</Drawer>
	);
}
